import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { configAPI, preferencesAPI } from '../api';
import { toast } from 'sonner';
import { ChevronRight, ChevronLeft, Check, Info } from 'lucide-react';

const PreferencesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [specialtyConfig, setSpecialtyConfig] = useState(null);
  
  // Form data
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [selectedSubspecialties, setSelectedSubspecialties] = useState([]); // Changed to array
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [customTopics, setCustomTopics] = useState('');
  const [selectedJournals, setSelectedJournals] = useState([]);
  const [customJournals, setCustomJournals] = useState('');
  const [frequency, setFrequency] = useState('daily');
  const [dayOfWeek, setDayOfWeek] = useState('Monday');
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [timeLocal, setTimeLocal] = useState('09:00');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [maxArticles, setMaxArticles] = useState(10);
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [suppressDuration, setSuppressDuration] = useState('');
  const [suppressUnit, setSuppressUnit] = useState('weeks');
  // Phase A v2: Reading goal
  const [readingGoalWeekly, setReadingGoalWeekly] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const config = await configAPI.getSpecialties();
      setSpecialtyConfig(config);
      
      try {
        const prefs = await preferencesAPI.getMyPreferences();
        if (prefs) {
          populateFormFromPreferences(prefs, config);
        }
      } catch (error) {
        console.log('No existing preferences found');
      }
    } catch (error) {
      console.error('Error loading config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const populateFormFromPreferences = (prefs, config) => {
    const specialty = config.specialties.find(s => s.id === prefs.specialty_id);
    if (specialty) {
      setSelectedSpecialty(specialty);
      
      // Handle multiple subspecialties
      if (prefs.subspecialties && prefs.subspecialties.length > 0) {
        const subspecs = prefs.subspecialties
          .map(id => specialty.subspecialties.find(sub => sub.id === id))
          .filter(Boolean);
        setSelectedSubspecialties(subspecs);
      } else if (prefs.subspecialty_id) {
        // Backward compatibility
        const subspecialty = specialty.subspecialties.find(sub => sub.id === prefs.subspecialty_id);
        if (subspecialty) {
          setSelectedSubspecialties([subspecialty]);
        }
      }
    }
    
    if (prefs.topics_selected) setSelectedTopics(prefs.topics_selected);
    if (prefs.custom_topics) setCustomTopics(prefs.custom_topics.join(', '));
    if (prefs.journals_selected) setSelectedJournals(prefs.journals_selected);
    if (prefs.custom_journals) setCustomJournals(prefs.custom_journals.join(', '));
    
    if (prefs.schedule) {
      setFrequency(prefs.schedule.frequency || 'daily');
      setTimeLocal(prefs.schedule.time_local || '09:00');
      setTimezone(prefs.schedule.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
      if (prefs.schedule.day_of_week) setDayOfWeek(prefs.schedule.day_of_week);
      if (prefs.schedule.day_of_month) setDayOfMonth(prefs.schedule.day_of_month);
    }
    
    if (prefs.max_articles_per_digest) setMaxArticles(prefs.max_articles_per_digest);
    if (prefs.advanced_preferences?.clinical_notes) setClinicalNotes(prefs.advanced_preferences.clinical_notes);
    if (typeof prefs.email_notifications_enabled === 'boolean') {
      setEmailNotificationsEnabled(prefs.email_notifications_enabled);
    }
    // Phase A v2: Reading goal
    if (typeof prefs.reading_goal_weekly === 'number') {
      setReadingGoalWeekly(prefs.reading_goal_weekly);
    }
  };

  const getAllTopics = () => {
    if (!selectedSubspecialties.length) return [];
    const topics = new Set();
    selectedSubspecialties.forEach(subspec => {
      (subspec.topics || []).forEach(topic => {
        const name = typeof topic === 'string' ? topic : topic.name;
        topics.add(name);
      });
    });
    return Array.from(topics);
  };

  // FIX: Changed from subspec.journals to subspec.top_journals
  const getAllJournals = () => {
    if (!selectedSubspecialties.length) return [];
    const journals = new Set();
    selectedSubspecialties.forEach(subspec => {
      (subspec.top_journals || []).forEach(journal => {
        const name = typeof journal === 'string' ? journal : journal.name;
        journals.add(name);
      });
    });
    return Array.from(journals);
  };

  const handleNext = () => {
    if (step === 1 && !selectedSpecialty) {
      toast.error('Please select a specialty');
      return;
    }
    // Subspecialty is now optional - removed validation
    if (step === 2 && selectedTopics.length === 0 && !customTopics.trim()) {
      toast.error('Please select at least one topic or enter custom topics');
      return;
    }
    if (step < 5) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const customTopicsList = customTopics.split(',').map(t => t.trim()).filter(t => t.length > 0);
      const customJournalsList = customJournals.split(',').map(j => j.trim()).filter(j => j.length > 0);
      
      const payload = {
        specialty_id: selectedSpecialty.id,
        subspecialties: selectedSubspecialties.map(s => s.id),
        topics_selected: selectedTopics,
        custom_topics: customTopicsList,
        journals_selected: selectedJournals,
        custom_journals: customJournalsList,
        max_articles_per_digest: maxArticles,
        schedule: {
          frequency,
          time_local: timeLocal,
          timezone,
          ...(frequency === 'weekly' && { day_of_week: dayOfWeek }),
          ...(frequency === 'monthly' && { day_of_month: dayOfMonth })
        },
        advanced_preferences: {
          clinical_notes: clinicalNotes
        },
        email_notifications_enabled: emailNotificationsEnabled,
        // Phase A v2: Reading goal
        reading_goal_weekly: readingGoalWeekly || null
      };
      
      if (suppressDuration && suppressUnit) {
        const now = new Date();
        const duration = parseInt(suppressDuration);
        if (suppressUnit === 'weeks') {
          now.setDate(now.getDate() + duration * 7);
        } else if (suppressUnit === 'months') {
          now.setMonth(now.getMonth() + duration);
        }
        payload.email_suppress_until = now.toISOString();
      }
      
      await preferencesAPI.createOrUpdatePreferences(payload);
      toast.success('Preferences saved successfully!');
      navigate('/home');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Set Your Preferences</h1>
          <p className="text-gray-600">Configure your personalized literature digest</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {[
              { num: 1, label: 'Specialty' },
              { num: 2, label: 'Topics' },
              { num: 3, label: 'Journals' },
              { num: 4, label: 'Schedule' },
              { num: 5, label: 'Advanced' }
            ].map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                      step === s.num
                        ? 'bg-blue-600 text-white'
                        : step > s.num
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {step > s.num ? <Check className="h-5 w-5" /> : s.num}
                  </div>
                  <span className="text-xs mt-1 text-gray-600">{s.label}</span>
                </div>
                {idx < 4 && (
                  <div
                    className={`flex-1 h-1 mx-2 rounded ${
                      step > s.num ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <Card>
          <CardContent className="p-6">
            <div className="min-h-[400px]">
              <form onSubmit={(e) => e.preventDefault()}>
                {/* Step 1: Specialty & Subspecialties */}
                {step === 1 && (
                  <div className="space-y-5">
                    <div>
                      <Label className="text-base font-semibold mb-2 block">Specialty *</Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {specialtyConfig?.specialties.map((specialty) => (
                          <div
                            key={specialty.id}
                            onClick={() => {
                              setSelectedSpecialty(specialty);
                              
                              // Auto-select General/Core if it exists
                              const generalCore = specialty.subspecialties?.find(
                                sub => sub.label.toLowerCase().includes('general') && 
                                       sub.label.toLowerCase().includes('core')
                              );
                              if (generalCore) {
                                setSelectedSubspecialties([generalCore]);
                              } else {
                                setSelectedSubspecialties([]);
                              }
                              
                              setSelectedTopics([]);
                              setSelectedJournals([]);
                            }}
                            className={`p-3 border-2 rounded-lg cursor-pointer text-sm ${
                              selectedSpecialty?.id === specialty.id
                                ? 'border-blue-600 bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300'
                            }`}
                          >
                            {specialty.label}
                          </div>
                        ))}
                      </div>
                    </div>

                    {selectedSpecialty && (
                      <div>
                        <Label className="text-base font-semibold mb-2 block">
                          Subspecialties (Optional)
                        </Label>
                        <p className="text-xs text-gray-500 mb-2">
                          <Info className="inline h-3 w-3 mr-1" />
                          Select one or more subspecialties, or skip to use general topics
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {selectedSpecialty.subspecialties?.map((subspecialty) => (
                            <div
                              key={subspecialty.id}
                              onClick={() => {
                                const isSelected = selectedSubspecialties.some(s => s.id === subspecialty.id);
                                if (isSelected) {
                                  setSelectedSubspecialties(selectedSubspecialties.filter(s => s.id !== subspecialty.id));
                                } else {
                                  setSelectedSubspecialties([...selectedSubspecialties, subspecialty]);
                                }
                                setSelectedTopics([]);
                                setSelectedJournals([]);
                              }}
                              className={`p-3 border-2 rounded-lg cursor-pointer text-sm ${
                                selectedSubspecialties.some(s => s.id === subspecialty.id)
                                  ? 'border-blue-600 bg-blue-50'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              {subspecialty.label}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2: Topics */}
                {step === 2 && (
                  <div className="space-y-5">
                    <div>
                      <Label className="text-base font-semibold mb-2 block">Topics of Interest *</Label>
                      {selectedSubspecialties.length > 0 ? (
                        <>
                          <p className="text-xs text-gray-500 mb-2">Select up to 15 topics</p>
                          <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                            {getAllTopics().map((topic) => (
                              <div
                                key={topic}
                                onClick={() => {
                                  if (selectedTopics.includes(topic)) {
                                    setSelectedTopics(selectedTopics.filter(t => t !== topic));
                                  } else if (selectedTopics.length < 15) {
                                    setSelectedTopics([...selectedTopics, topic]);
                                  } else {
                                    toast.error('Maximum 15 topics allowed');
                                  }
                                }}
                                className={`p-2 border-2 rounded-md cursor-pointer text-sm ${
                                  selectedTopics.includes(topic)
                                    ? 'border-blue-600 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300'
                                }`}
                              >
                                {topic}
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Selected: {selectedTopics.length}/15
                          </p>
                        </>
                      ) : (
                        <p className="text-sm text-gray-500 italic">Select a subspecialty to see available topics, or enter custom topics below.</p>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm">Custom Topics (Optional)</Label>
                      <Textarea
                        placeholder="Additional topics"
                        value={customTopics}
                        onChange={(e) => setCustomTopics(e.target.value)}
                        rows={3}
                        className="mt-1 text-sm"
                      />
                    </div>
                  </div>
                )}

                {/* Step 3: Journals */}
                {step === 3 && (
                  <div className="space-y-5">
                    <div>
                      <Label className="text-base font-semibold mb-2 block">Journals</Label>
                      {selectedSubspecialties.length > 0 && getAllJournals().length > 0 ? (
                        <>
                          <p className="text-xs text-gray-500 mb-2">Select preferred journals (up to 10)</p>
                          <div className="space-y-1.5">
                            {getAllJournals().map((journal) => (
                              <div
                                key={journal}
                                onClick={() => {
                                  if (selectedJournals.includes(journal)) {
                                    setSelectedJournals(selectedJournals.filter(j => j !== journal));
                                  } else {
                                    setSelectedJournals([...selectedJournals, journal]);
                                  }
                                }}
                                className={`p-2 border-2 rounded-md cursor-pointer text-sm ${
                                  selectedJournals.includes(journal)
                                    ? 'border-blue-600 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300'
                                }`}
                              >
                                {journal}
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <p className="text-sm text-gray-500 italic mb-3">
                          No curated journals available for selected subspecialties. You can still enter custom journals below.
                        </p>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm">Custom Journals (Optional)</Label>
                      <Textarea
                        placeholder="Additional journals"
                        value={customJournals}
                        onChange={(e) => setCustomJournals(e.target.value)}
                        rows={2}
                        className="mt-1 text-sm"
                      />
                    </div>
                  </div>
                )}

                {/* Step 4: Schedule */}
                {step === 4 && (
                  <div className="space-y-5">
                    <div>
                      <Label className="text-base font-semibold mb-2 block">Digest Frequency</Label>
                      <div className="flex gap-2">
                        {['daily', 'weekly', 'monthly'].map((freq) => (
                          <Button
                            key={freq}
                            type="button"
                            variant={frequency === freq ? 'default' : 'outline'}
                            onClick={() => setFrequency(freq)}
                            className="flex-1 capitalize"
                          >
                            {freq}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {frequency === 'weekly' && (
                      <div>
                        <Label className="text-sm mb-2 block">Day of Week</Label>
                        <select
                          value={dayOfWeek}
                          onChange={(e) => setDayOfWeek(e.target.value)}
                          className="w-full p-2 border rounded-md"
                        >
                          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(
                            (day) => (
                              <option key={day} value={day}>
                                {day}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                    )}

                    {frequency === 'monthly' && (
                      <div>
                        <Label className="text-sm mb-2 block">Day of Month</Label>
                        <Input
                          type="number"
                          min="1"
                          max="28"
                          value={dayOfMonth}
                          onChange={(e) => setDayOfMonth(parseInt(e.target.value))}
                        />
                      </div>
                    )}

                    <div>
                      <Label className="text-sm mb-2 block">Time (Local)</Label>
                      <Input type="time" value={timeLocal} onChange={(e) => setTimeLocal(e.target.value)} />
                    </div>

                    <div>
                      <Label className="text-sm mb-2 block">Timezone</Label>
                      <Input value={timezone} disabled className="bg-gray-100" />
                    </div>

                    <div>
                      <Label className="text-sm mb-2 block">Max Articles per Digest</Label>
                      <Input
                        type="number"
                        min="5"
                        max="20"
                        value={maxArticles}
                        onChange={(e) => setMaxArticles(parseInt(e.target.value))}
                      />
                    </div>

                    {/* Phase A v2: Reading Goal */}
                    <div>
                      <Label className="text-sm mb-2 block">Weekly Reading Goal (Optional)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        placeholder="e.g., 5 articles per week"
                        value={readingGoalWeekly || ''}
                        onChange={(e) => setReadingGoalWeekly(parseInt(e.target.value) || 0)}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Set a goal to track your weekly reading progress
                      </p>
                    </div>
                  </div>
                )}

                {/* Step 5: Advanced */}
                {step === 5 && (
                  <div className="space-y-5">
                    <div>
                      <Label className="text-base font-semibold mb-2 block">Clinical Notes (Optional)</Label>
                      <Textarea
                        placeholder="Any specific clinical interests or focus areas"
                        value={clinicalNotes}
                        onChange={(e) => setClinicalNotes(e.target.value)}
                        rows={4}
                      />
                    </div>

                    <div>
                      <Label className="text-base font-semibold mb-2 block">Email Notifications</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={emailNotificationsEnabled}
                          onChange={(e) => setEmailNotificationsEnabled(e.target.checked)}
                          className="h-4 w-4"
                        />
                        <span className="text-sm">Enable email notifications for new digests</span>
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm mb-2 block">Temporarily Suppress Emails</Label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          placeholder="Duration"
                          value={suppressDuration}
                          onChange={(e) => setSuppressDuration(e.target.value)}
                          className="flex-1"
                        />
                        <select
                          value={suppressUnit}
                          onChange={(e) => setSuppressUnit(e.target.value)}
                          className="flex-1 p-2 border rounded-md"
                        >
                          <option value="weeks">Weeks</option>
                          <option value="months">Months</option>
                        </select>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Optionally pause digest emails for a period
                      </p>
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={step === 1}
                className="flex items-center gap-2"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </Button>

              {step < 5 ? (
                <Button type="button" onClick={handleNext} className="flex items-center gap-2">
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                >
                  {saving ? 'Saving...' : 'Save Preferences'}
                  <Check className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PreferencesPage;
